// Types for BridgeFT admin endpoints
// Matches backend DTOs from AdminBridgeftReportDto.ts and apexAccountPerformanceDto.ts

export interface AdminBridgeftAccountMetadataDto {
    apexAccountId: string | null;
    bridgeftAccountId: string;
    accountNumber: string;
}

export interface AdminBridgeftDepositWithdrawalItemDto {
    transactionDate: string | null;
    reportedDate: string | null;
    description: string | null;
    securityDescription: string | null;
    amount: number | null;
    type: string | null;
    isCancel: boolean | null;
}

export interface AdminBridgeftDepositWithdrawalDto {
    items: AdminBridgeftDepositWithdrawalItemDto[];
    total: number | null;
}

export interface AdminBridgeftBuySellItemDto {
    securityDescription: string | null;
    lucaData: object | null;
}

export interface AdminBridgeftBuySellDto {
    items: AdminBridgeftBuySellItemDto[];
    total: number | null;
}

export interface AdminBridgeftIncomeItemDto {
    transactionDate: string | null;
    reportedDate: string | null;
    description: string | null;
    securityDescription: string | null;
    amount: number | null;
    type: string | null;
    isCancel: boolean | null;
    isPerformanceImpact: boolean | null;
}

export interface AdminBridgeftIncomeDto {
    items: AdminBridgeftIncomeItemDto[];
    total: number | null;
}

export interface AdminBridgeftDatapointDto {
    time: string;
    value: number;
}

export interface AdminBridgeftChartSeriesDto {
    frequency?: string;
    beginning_date?: string;
    balance?: {
        id?: string;
        name?: string;
        points: AdminBridgeftDatapointDto[];
    };
    cumulative_contribution?: {
        id?: string;
        name?: string;
        points: AdminBridgeftDatapointDto[];
    };
    inception_return?: {
        id?: string;
        name?: string;
        points: AdminBridgeftDatapointDto[] | null;
    };
    period_return?: {
        id?: string;
        name?: string;
        points: AdminBridgeftDatapointDto[] | null;
    };
    [key: string]: unknown;
}

export interface AdminBridgeftAccountSummaryPeriodDto {
    beginning_balance?: number;
    ending_balance?: number;
    beginning_balance_date?: string;
    ending_balance_date?: string;
    income?: number;
    contributions?: number;
    withdrawals?: number;
    net_contributions?: number;
    net_appreciation?: number;
    net_gain?: number;
    [key: string]: unknown;
}

export interface AdminBridgeftAccountSummaryPerformanceDto {
    date?: string;
    ptd_return?: number;
    mtd_return?: number;
    qtd_return?: number;
    ytd_return?: number;
    itd_return?: number;
    itd_annual_return?: number | null;
    three_yr_return?: number | null;
    five_yr_return?: number | null;
    ltm_return?: number;
    [key: string]: unknown;
}

/**
 * BridgeFT sub_reports REQUEST names vs RESPONSE keys differ:
 *
 *   Request name          Response key in sub_report_data
 *   ────────────────────  ────────────────────────────────
 *   account_summary       account_summary
 *   net_investment_chart   NO separate key — chart data (daily_itd_chart,
 *                          weekly_itd_chart) is merged INTO account_summary
 *   deposits_withdrawals  deposit_withdrawal   (no trailing 's')
 *   buy_sells             buysell              (no underscore, no plural)
 *   income                income_expense
 *
 * When net_investment_chart and account_summary are both requested, the
 * account_summary response object contains BOTH the performance fields
 * (period, latest_performance) AND the chart fields (daily_itd_chart,
 * weekly_itd_chart).
 */
export interface AdminBridgeftAccountSummaryDto {
    inception?: AdminBridgeftAccountSummaryPeriodDto;
    period?: AdminBridgeftAccountSummaryPeriodDto;
    latest_performance?: AdminBridgeftAccountSummaryPerformanceDto;
    performance?: Array<
        AdminBridgeftAccountSummaryPerformanceDto & { date: string }
    >;
    daily_itd_chart?: AdminBridgeftChartSeriesDto;
    weekly_itd_chart?: AdminBridgeftChartSeriesDto;
    monthly_itd_chart?: AdminBridgeftChartSeriesDto;
    daily_ptd_chart?: AdminBridgeftChartSeriesDto;
    weekly_ptd_chart?: AdminBridgeftChartSeriesDto;
    monthly_ptd_chart?: AdminBridgeftChartSeriesDto;
    period_has_data?: boolean;
    [key: string]: unknown;
}

export interface AdminBridgeftReportResponseDto {
    account: AdminBridgeftAccountMetadataDto;
    dateRange: { start: string; end: string };
    requestedSubReports: string[];
    subReports: {
        account_summary?: AdminBridgeftAccountSummaryDto;
        deposit_withdrawal?: AdminBridgeftDepositWithdrawalDto;
        buysell?: AdminBridgeftBuySellDto;
        income_expense?: AdminBridgeftIncomeDto;
        [key: string]: unknown;
    };
}

export enum ApexPerformanceHorizon {
    '1M' = '1M',
    '3M' = '3M',
    '6M' = '6M',
    '1Y' = '1Y',
    ALL = 'ALL',
}

export interface ApexPerformanceDatapointDto {
    step: string;
    netContribution: number;
    accountValuation: number;
}

export interface ApexPerformanceDateGap {
    from: string;
    to: string;
    series: 'balance' | 'contribution' | 'both';
    missingDays?: number;
    missingWeeks?: number;
}

export interface ApexPerformanceZeroDrop {
    step: string;
    previousValue: number;
    netContribution: number;
}

export type ApexPerformanceError =
    | {
          type: 'DATE_GAP';
          message: string;
          gaps: ApexPerformanceDateGap[];
      }
    | {
          type: 'VALUE_DROP_TO_ZERO';
          message: string;
          occurrences: ApexPerformanceZeroDrop[];
      };

export interface ApexAccountPerformanceSeriesItemDto {
    horizon: ApexPerformanceHorizon;
    amountReturn: number;
    percentageReturn: number | null;
    datapoints: ApexPerformanceDatapointDto[];
}

export type ApexPerformanceByHorizon = Record<
    ApexPerformanceHorizon,
    ApexAccountPerformanceSeriesItemDto
>;

export type ApexPerformanceByHorizonResponse = ApexPerformanceByHorizon & {
    errors: ApexPerformanceError[];
};

export interface AdminSyncPerformanceResponseDto {
    workflowID: string;
}

export interface AdminResetBridgeftHouseholdsResponseDto {
    membershipsFound: number;
    householdsDeleted: number;
    membershipsCleared: number;
    membershipsFailed: number;
}
